import { Component } from '@theme/component';
import { ThemeEvents, QuantitySelectorUpdateEvent } from '@theme/events';

/**
 * Connects tier cards to the quantity selector inside the same product form.
 * Cart /add uses the form's input[name="quantity"] — we must set that input
 * with values that respect min, step, max and effective max (max − cart qty).
 *
 * @typedef {Object} PSPRefs
 * @property {HTMLElement} mengeValue
 * @property {HTMLButtonElement} tier1
 * @property {HTMLButtonElement} tier2
 * @property {HTMLButtonElement} tier3
 */
class ProductSetPricing extends Component {
  /** @type {HTMLElement | null} */
  #section = null;

  /** @type {AbortController | null} */
  #tierClickAbort = null;

  /** @type {{ qty: number; stk: string; el: HTMLButtonElement }[]} */
  #tiers = [];

  connectedCallback() {
    super.connectedCallback();
    this.#section = this.closest('.shopify-section');

    this.#bindTierButtons();
    this.#syncFromQty(this.#readCurrentQty());

    this.#section?.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#onQtyEvent);
    this.#section?.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#tierClickAbort?.abort();
    this.#tierClickAbort = null;
    this.#section?.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#onQtyEvent);
    this.#section?.removeEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate);
  }

  /** Tiers per DOM (nicht nur ref), damit Klicks immer gebunden sind */
  #bindTierButtons() {
    this.#tierClickAbort?.abort();
    this.#tierClickAbort = new AbortController();
    const { signal } = this.#tierClickAbort;

    const buttons = this.querySelectorAll('button.psp-tier[data-tier]');
    this.#tiers = [...buttons].map((el) => {
      const b = /** @type {HTMLButtonElement} */ (el);
      return {
        qty: Number(b.dataset.qty ?? 1),
        stk: b.dataset.stk ?? '',
        el: b,
      };
    });

    this.addEventListener(
      'click',
      (e) => {
        const btn = e.target instanceof Element ? e.target.closest('button.psp-tier') : null;
        if (!(btn instanceof HTMLButtonElement) || !this.contains(btn)) return;
        const tier = this.#tiers.find((t) => t.el === btn);
        if (!tier) return;
        e.preventDefault();
        this.#selectTier(tier);
      },
      { capture: true, signal }
    );
  }

  /** Nach Varianten-Wechsel Preis erneut auf aktuelle Stufe setzen */
  #onVariantUpdate = (event) => {
    if (!(event instanceof CustomEvent)) return;
    const pid = this.dataset.productId;
    const data = event.detail?.data;
    if (data?.productId != null && String(data.productId) !== String(pid)) return;
    requestAnimationFrame(() => this.#syncFromQty(this.#readCurrentQty()));
  };

  /**
   * @param {string} str
   * @returns {number}
   */
  #parseDisplayPrice(str) {
    const cleaned = String(str ?? '')
      .trim()
      .replace(/[^\d,.-]/g, '');
    if (!cleaned) return Number.NaN;
    const lastComma = cleaned.lastIndexOf(',');
    const lastDot = cleaned.lastIndexOf('.');
    let normalized;
    if (lastComma > lastDot) {
      normalized = cleaned.replace(/\./g, '').replace(',', '.');
    } else if (lastDot > lastComma) {
      normalized = cleaned.replace(/,/g, '');
    } else {
      normalized = cleaned.replace(',', '.');
    }
    const n = parseFloat(normalized);
    return Number.isFinite(n) ? n : Number.NaN;
  }

  /**
   * @param {number} pct
   * @returns {string}
   */
  #discountBadgeHtml(pct) {
    const tpl = this.dataset.saveBadgeTemplate ?? '';
    const text = tpl ? tpl.replace('[P]', String(pct)) : `You save ${pct}%`;
    const esc = (/** @type {string} */ str) => {
      const d = document.createElement('div');
      d.textContent = str;
      return d.innerHTML;
    };
    return `<span class="price-discount-badge">${esc(text)}</span>`;
  }

  /**
   * Markup wie im Snippet `price.liquid` (sale / compare), inkl. .price__regular / .price__sale.
   */
  #buildTierPriceMarkup(saleRaw, compareRaw, saleFirst, labelSale, labelRegular) {
    const esc = (/** @type {string} */ str) => {
      const d = document.createElement('div');
      d.textContent = str;
      return d.innerHTML;
    };

    const s = esc(saleRaw);
    const c = compareRaw ? esc(compareRaw) : '';
    const ls = esc(labelSale);
    const lr = esc(labelRegular);

    let badge = '';
    if (c) {
      const saleNum = this.#parseDisplayPrice(saleRaw);
      const compareNum = this.#parseDisplayPrice(compareRaw);
      if (compareNum > 0 && Number.isFinite(saleNum) && compareNum > saleNum) {
        const pct = Math.round(((compareNum - saleNum) / compareNum) * 100);
        if (pct >= 1) {
          badge = this.#discountBadgeHtml(pct);
        }
      }
    }

    const badgeClass = badge ? ' price__sale--with-badge' : '';

    if (!c) {
      return `<div class="price__regular"><span class="price">${s}</span></div><div class="price__sale price__hidden"></div>`;
    }

    if (saleFirst) {
      return `<div class="price__regular price__hidden"><span class="price">${s}</span></div><div class="price__sale${badgeClass}"><span class="price__sale-prices"><span class="price-item__group"><span class="visually-hidden">${ls}&nbsp;</span><span class="price-item--sale price">${s}</span></span><span class="price-item__group"><span class="visually-hidden">${lr}&nbsp;</span><span class="price-item--regular compare-at-price">${c}</span></span></span>${badge}</div>`;
    }

    return `<div class="price__regular price__hidden"><span class="price">${s}</span></div><div class="price__sale${badgeClass}"><span class="price__sale-prices"><span class="price-item__group"><span class="visually-hidden">${lr}&nbsp;</span><span class="price-item--regular compare-at-price">${c}</span></span><span class="price-item__group"><span class="visually-hidden">${ls}&nbsp;</span><span class="price-item--sale price">${s}</span></span></span>${badge}</div>`;
  }

  /** @param {{ el: HTMLButtonElement }} matched */
  #syncMainProductPrice(matched) {
    if (this.dataset.syncMainPrice === 'false') return;

    const sale = matched.el.dataset.salePrice?.trim() ?? '';
    if (!sale) return;

    const compare = matched.el.dataset.comparePrice?.trim() ?? '';
    const saleFirst = this.dataset.saleFirst !== 'false';
    const labelSale = this.dataset.labelSale || 'Sale price';
    const labelRegular = this.dataset.labelRegular || 'Regular price';
    const html = this.#buildTierPriceMarkup(sale, compare, saleFirst, labelSale, labelRegular);
    const pid = this.dataset.productId;

    const hosts = this.#section?.querySelectorAll(`product-price[data-product-id="${pid}"]`) ?? [];
    for (const host of hosts) {
      const container = host.querySelector?.('[ref="priceContainer"]');
      if (!(container instanceof HTMLElement)) continue;
      const unit = container.querySelector('small.unit-price');
      const unitHtml = unit instanceof HTMLElement ? unit.outerHTML : '';
      container.innerHTML = html + unitHtml;
    }

    const stickyPc = this.#section?.querySelector('.sticky-add-to-cart__price [ref="priceContainer"]');
    if (stickyPc instanceof HTMLElement) {
      const u = stickyPc.querySelector('small.unit-price');
      const uHtml = u instanceof HTMLElement ? u.outerHTML : '';
      stickyPc.innerHTML = html + uHtml;
    }
  }

  /** @returns {HTMLElement | null} */
  #getProductForm() {
    const productId = this.dataset.productId;
    if (!productId || !this.#section) return null;
    return /** @type {HTMLElement | null} */ (
      this.#section.querySelector(`product-form-component[data-product-id="${productId}"]`)
    );
  }

  /**
   * @returns {HTMLElement & {
   *   setValue: (v: string) => void;
   *   onQuantityChange: () => void;
   *   updateButtonStates?: () => void;
   *   getCurrentValues?: () => { min: number; max: number | null; step: number; value: number; cartQuantity: number };
   *   getEffectiveMax?: () => number | null;
   * } | null}
   */
  #getQtyComponent() {
    const form = this.#getProductForm();
    if (!form) return null;
    return /** @type {any} */ (form.querySelector('quantity-selector-component'));
  }

  /** @returns {HTMLInputElement | null} */
  #getQtyInput() {
    const root = this.#getProductForm();
    if (!root) return null;
    const form = /** @type {HTMLFormElement | null} */ (
      root.querySelector('form[data-type="add-to-cart-form"]') ?? root.querySelector('form')
    );
    if (form) {
      const named = form.querySelector('input[name="quantity"]');
      if (named instanceof HTMLInputElement) return named;
    }
    return /** @type {HTMLInputElement | null} */ (root.querySelector('input[name="quantity"]'));
  }

  /**
   * Wenn der Mengen-Block im Theme ausgeblendet/fehlt, gibt es kein input[name="quantity"] —
   * dann legen wir ein verstecktes Feld im Add-to-Cart-Formular an (Chrome-Test: sonst bleibt der Switch tot).
   */
  #getOrCreateQtyInput() {
    const existing = this.#getQtyInput();
    if (existing) return existing;

    const formRoot = this.#getProductForm();
    if (!formRoot) return null;

    const form = /** @type {HTMLFormElement | null} */ (
      formRoot.querySelector('form[data-type="add-to-cart-form"]') ?? formRoot.querySelector('form')
    );
    if (!(form instanceof HTMLFormElement)) return null;
    if (form.querySelector('input[name="quantity"]')) {
      return /** @type {HTMLInputElement} */ (form.querySelector('input[name="quantity"]'));
    }

    const input = document.createElement('input');
    input.type = 'hidden';
    input.name = 'quantity';
    const def = formRoot.dataset.quantityDefault || '1';
    input.value = def;
    input.setAttribute('min', def);
    input.setAttribute('data-min', def);
    input.setAttribute('step', formRoot.dataset.quantityStep || '1');
    const max = formRoot.dataset.quantityMax;
    if (max != null && String(max) !== '') {
      input.setAttribute('max', String(max));
    }
    input.setAttribute('data-cart-quantity', '0');
    form.appendChild(input);
    return input;
  }

  /**
   * Snap desired cart-line quantity to Shopify quantity_rule + room left to add.
   * @param {number} desired
   * @param {HTMLInputElement} input
   */
  #snapDesiredQuantity(desired, input) {
    const min = parseInt(input.getAttribute('min') || input.dataset.min || '1', 10) || 1;
    const step = parseInt(input.getAttribute('step') || '1', 10) || 1;
    const maxAttr = input.getAttribute('max');
    const max = maxAttr != null && maxAttr !== '' ? parseInt(maxAttr, 10) : null;
    const cartQty = parseInt(input.getAttribute('data-cart-quantity') || '0', 10) || 0;

    let effectiveMax = null;
    if (max !== null && !Number.isNaN(max)) {
      effectiveMax = Math.max(max - cartQty, min);
    }

    let v = min + Math.round((desired - min) / step) * step;
    v = Math.max(min, v);
    if (effectiveMax !== null && !Number.isNaN(effectiveMax)) {
      v = Math.min(v, effectiveMax);
      if (v < min) v = min;
      if ((v - min) % step !== 0) {
        v = min + Math.floor((v - min) / step) * step;
        v = Math.max(min, v);
      }
    }
    return v;
  }

  /** @param {number} desired */
  #applyQuantity(desired) {
    const qtyComp = this.#getQtyComponent();
    const input = qtyComp?.refs?.quantityInput ?? this.#getOrCreateQtyInput();

    if (!(input instanceof HTMLInputElement)) return;

    const snapped = this.#snapDesiredQuantity(desired, input);

    if (qtyComp && typeof qtyComp.setValue === 'function') {
      qtyComp.setValue(String(snapped));
      qtyComp.onQuantityChange();
      if (typeof qtyComp.updateButtonStates === 'function') {
        qtyComp.updateButtonStates();
      }
    } else {
      input.value = String(snapped);
      input.dispatchEvent(new Event('input', { bubbles: true }));
      input.dispatchEvent(new Event('change', { bubbles: true }));
      input.dispatchEvent(new QuantitySelectorUpdateEvent(snapped));
    }
  }

  /** @param {{ qty: number; stk: string; el: HTMLButtonElement }} tier */
  #selectTier(tier) {
    this.#applyQuantity(tier.qty);
    this.#syncFromQty(this.#readCurrentQty());
  }

  /** @param {Event} e */
  #onQtyEvent = (e) => {
    if (!(e instanceof CustomEvent)) return;
    /** für Warenkorb-Zeilenselektoren ignorieren */
    if (e.detail?.cartLine != null) return;

    const form = this.#getProductForm();
    const t = e.target;
    if (!(t instanceof Node) || !form?.contains(t)) return;

    const qty = e.detail?.quantity;
    if (qty != null) this.#syncFromQty(Number(qty));
  };

  /** @param {number} qty */
  #syncFromQty(qty) {
    let matched = this.#tiers[0];
    for (const t of this.#tiers) {
      if (qty >= t.qty) matched = t;
    }

    this.#tiers.forEach((t) => {
      const active = t === matched;
      t.el.classList.toggle('psp-tier--active', active);
      t.el.setAttribute('aria-pressed', active ? 'true' : 'false');
    });

    const mengeEl = this.refs.mengeValue;
    if (mengeEl instanceof HTMLElement) mengeEl.textContent = matched.stk;

    this.#syncMainProductPrice(matched);
  }

  /** @returns {number} */
  #readCurrentQty() {
    const input = this.#getQtyInput();
    const n = Number(input?.value);
    return Number.isFinite(n) && n > 0 ? n : 1;
  }
}

if (!customElements.get('product-set-pricing')) {
  customElements.define('product-set-pricing', ProductSetPricing);
}
